import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, X } from 'lucide-react';
import ThemeToggle from './components/ThemeToggle';
import AnalysisPage from './AnalysisPage';
import TestRigPage from './TestRigPage';
import HistoryPage from './HistoryPage';
import AnnotationPage from './AnnotationPage';
import AnalyzeWebsitePage from './AnalyzeWebsitePage';
import ScraperEmbedPage from './ScraperEmbedPage';
import UsersAdminPage from './UsersAdminPage';
import ProfilePage from './ProfilePage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import LoginGate from './LoginGate';
import { RuntimeSettingsProvider } from './context/RuntimeSettingsContext';
import { AuthProvider, useAuth, hasRole } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// The app is served under this path (nginx: auction.ai/engine/). React Router's
// basename keeps every in-app URL under it, instead of the router treating the
// apex as root and collapsing the address bar to auction.ai/ on navigation.
const ROUTER_BASENAME = '/engine';

// Role-based route guard. Renders children only if `role` is allowed.
// `superuser` is implicitly allowed everywhere.
const RequireRole = ({ allowed, children }) => {
  const { role } = useAuth();
  if (!hasRole(role, ...allowed)) {
    // Redirect to a page they CAN see.
    if (role === 'annotator') return <Navigate to="/annotation" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
};

const NavBar = () => {
  const { role, user, logout } = useAuth();
  const isAnnotator = role === 'annotator';
  const isUserOrUp = hasRole(role, 'user');
  const isSuper = role === 'superuser';
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  // Primary links shown inline on desktop.
  const links = [];
  if (isUserOrUp) {
    links.push(
      { to: '/', label: 'Scan' },
      { to: '/history', label: 'History' },
      { to: '/analyze-website', label: 'Analyse web-site' },
      { to: '/scraper', label: 'Scraper' },
    );
  }
  if (isAnnotator) links.push({ to: '/annotation', label: 'Annotation tool' });
  links.push({ to: '/profile', label: 'Profile' });

  // Admin-only extras collapse into a "More" menu so the bar stays uncrowded.
  const adminLinks = [];
  if (isSuper) {
    adminLinks.push(
      { to: '/test-rig', label: 'Test Rig' },
      { to: '/annotation', label: 'Annotation tool' },
      { to: '/users', label: 'Users' },
    );
  }

  // Close menus whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  // Close the "More" dropdown on an outside click.
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [moreOpen]);

  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));
  const linkClass = (to) =>
    `text-sm transition-colors ${isActive(to) ? 'text-brand font-medium' : 'text-muted hover:text-ink'}`;

  return (
    <nav className="bg-surface border-b border-hairline shadow-sm mb-4 sm:mb-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-3">
        <Link to="/" className="font-bold text-lg sm:text-xl text-brand select-none shrink-0">
          Auction.ai
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-5 min-w-0">
          {links.map((l) => (
            <Link key={l.to + l.label} to={l.to} className={linkClass(l.to)}>{l.label}</Link>
          ))}
          {adminLinks.length > 0 && (
            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink transition-colors"
                aria-haspopup="true"
                aria-expanded={moreOpen}
              >
                More <ChevronDown size={15} className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 mt-2 w-44 py-1 rounded-lg border border-hairline bg-surface shadow-lg z-50">
                  {adminLinks.map((l) => (
                    <Link
                      key={l.to + l.label}
                      to={l.to}
                      className={`block px-3 py-2 text-sm transition-colors ${isActive(l.to) ? 'text-brand' : 'text-muted hover:text-ink hover:bg-surface-2'}`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {user?.username && (
            <span className="text-xs text-muted hidden xl:inline">
              {user.username}
              <span className="px-1.5 py-0.5 ml-1.5 rounded bg-surface-2 text-muted">{role}</span>
            </span>
          )}
          <button
            onClick={logout}
            className="hidden lg:inline-flex items-center gap-1 text-sm text-muted hover:text-red-500 transition-colors"
          >
            <LogOut size={15} /> Log out
          </button>
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-8 h-8 rounded-lg text-muted hover:text-brand hover:bg-surface-2 transition-colors"
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-hairline bg-surface">
          <div className="max-w-6xl mx-auto px-3 py-2 flex flex-col">
            {[...links, ...adminLinks].map((l) => (
              <Link
                key={`m-${l.to}-${l.label}`}
                to={l.to}
                className={`py-2 text-sm ${isActive(l.to) ? 'text-brand font-medium' : 'text-muted hover:text-ink'}`}
              >
                {l.label}
              </Link>
            ))}
            {user?.username && (
              <div className="py-2 mt-1 text-xs text-muted border-t border-hairline">
                {user.username}
                <span className="px-1.5 py-0.5 ml-1.5 rounded bg-surface-2 text-muted">{role}</span>
              </div>
            )}
            <button
              onClick={logout}
              className="py-2 text-left text-sm text-muted hover:text-red-500 transition-colors inline-flex items-center gap-1"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

const AppShell = () => {
  const { role } = useAuth();
  const annotatorOnly = role === 'annotator';

  return (
    <Router basename={ROUTER_BASENAME}>
      <div className="min-h-screen bg-canvas text-ink">
        <NavBar />
        <main className="container mx-auto px-2 sm:px-4 pb-8 sm:pb-12">
          <Routes>
            {/* Annotator's "home" is the annotation tool. */}
            <Route
              path="/"
              element={
                annotatorOnly
                  ? <Navigate to="/annotation" replace />
                  : <RequireRole allowed={['user']}><AnalysisPage /></RequireRole>
              }
            />
            <Route
              path="/history"
              element={<RequireRole allowed={['user']}><HistoryPage /></RequireRole>}
            />
            <Route
              path="/analyze-website"
              element={<RequireRole allowed={['user']}><AnalyzeWebsitePage /></RequireRole>}
            />
            <Route
              path="/scraper"
              element={<RequireRole allowed={['user']}><ScraperEmbedPage /></RequireRole>}
            />
            <Route
              path="/test-rig"
              element={<RequireRole allowed={[]}><TestRigPage /></RequireRole>}
            />
            <Route
              path="/annotation"
              element={<RequireRole allowed={['annotator']}><AnnotationPage /></RequireRole>}
            />
            <Route
              path="/users"
              element={<RequireRole allowed={[]}><UsersAdminPage /></RequireRole>}
            />
            <Route
              path="/profile"
              element={<RequireRole allowed={['user', 'annotator']}><ProfilePage /></RequireRole>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
};

function App() {
  let path = (
    typeof window !== 'undefined'
      ? String(window.location.pathname || '')
      : ''
  );
  // This check runs BEFORE the Router, so strip the mount prefix ourselves —
  // otherwise the path is "/engine/privacy-policy" and never matches.
  if (ROUTER_BASENAME && path.startsWith(ROUTER_BASENAME)) {
    path = path.slice(ROUTER_BASENAME.length) || '/';
  }
  const normalizedPath = path.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/privacy-policy') {
    return (
      <ThemeProvider>
        <PrivacyPolicyPage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <LoginGate>
          <RuntimeSettingsProvider>
            <AppShell />
          </RuntimeSettingsProvider>
        </LoginGate>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
